/**
 * qualityUtils.js — Service Quality scoring (pure).
 */
import { STALE_MINUTES, DELAY_THRESHOLD_MIN, QUALITY_FLAGS, QUALITY_SCORE_WEIGHTS } from '../ticketConstants';

const BAD_FLAG_KEYS = new Set(QUALITY_FLAGS.filter(f => f.bad).map(f => f.key));
const FLAG_WEIGHT = Object.fromEntries(QUALITY_FLAGS.map(f => [f.key, f.weight]));

/** Flags currently tagged on a record (always an array). */
export function getQualityFlags(record) {
  const flags = record?.quality?.flags;
  return Array.isArray(flags) ? flags : [];
}

/** True if the record carries any score-lowering (bad) flag. */
export function hasBadQuality(record) {
  return getQualityFlags(record).some(f => BAD_FLAG_KEYS.has(f));
}

/**
 * On-time test for a completed record.
 * @returns {boolean|null} true/false, or null when timing is unknown.
 */
export function isOnTime(record, threshold = DELAY_THRESHOLD_MIN) {
  const t = record?.timeTakenMin;
  if (t == null) return null;
  return t <= threshold;
}

/**
 * Compute a 0–100 Service Quality Score over a set of completed records.
 * Blends three rates (weights in QUALITY_SCORE_WEIGHTS):
 *   • onTime     — completed within the delay threshold
 *   • defectFree — free of bad quality flags (severity-weighted)
 *   • notStale   — not abandoned/very-late (< STALE_MINUTES to complete)
 * Returns the score plus the component rates and per-flag counts.
 */
export function computeQualityScore(records, threshold = DELAY_THRESHOLD_MIN) {
  const list = Array.isArray(records) ? records : [];
  const total = list.length;
  if (total === 0) {
    return { score: null, total: 0, onTimeRate: null, defectFreeRate: null,
      notStaleRate: null, defectOrders: 0, praiseOrders: 0, counts: {}, timed: 0, onTime: 0, late: 0 };
  }

  let timed = 0, onTime = 0, notStale = 0, defectLoad = 0, defectOrders = 0, praiseOrders = 0;
  const counts = {};
  QUALITY_FLAGS.forEach(f => { counts[f.key] = 0; });

  list.forEach(r => {
    const t = r?.timeTakenMin;
    if (t != null) {
      timed++;
      if (t <= threshold) onTime++;
      if (t < STALE_MINUTES) notStale++;
    }
    const flags = getQualityFlags(r);
    flags.forEach(f => { if (counts[f] != null) counts[f]++; });
    const badOnOrder = flags.filter(f => BAD_FLAG_KEYS.has(f));
    if (badOnOrder.length) {
      defectOrders++;
      // one order's penalty = summed flag weights, capped so a single order can't tank the score
      const penalty = Math.min(2, badOnOrder.reduce((s, f) => s + (FLAG_WEIGHT[f] || 1), 0));
      defectLoad += penalty;
    }
    if (flags.includes('praise')) praiseOrders++;
  });

  const onTimeRate = timed > 0 ? onTime / timed : 1;
  const notStaleRate = timed > 0 ? notStale / timed : 1;
  const defectFreeRate = Math.max(0, 1 - defectLoad / total);

  const w = QUALITY_SCORE_WEIGHTS;
  const score = Math.round(
    100 * (w.onTime * onTimeRate + w.defectFree * defectFreeRate + w.notStale * notStaleRate)
  );

  return {
    score, total, timed, onTime, late: timed - onTime,
    onTimeRate, defectFreeRate, notStaleRate,
    defectOrders, praiseOrders, counts,
  };
}
